import { JSONForm } from '@conventioncatcorp/common-fe';
import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { OptionDataPayload } from '../../../../shared/options';
import { ProductModel, ProductOptionInput } from '../../../../shared/orders';
import { raffleTicketStatus } from '../../../../shared/orders/raffle';
import {
  RegistrationFlagModel,
  RegistrationOptionInput,
} from '../../../../shared/registration/model';
import { RegistrationTicket, UserRegistration } from '../../../../shared/user/base';
import { ActionButtonModal, AttendanceFlags, ProductOptionViewer } from '../../../components';
import { UserSelector } from '../../../components/UserSelector';
import { fthrow, isAccessError, useNumericInputState } from '../../../utils';
import { LoadingWrapper } from '../../../utils/LoadingWrapper';

async function getProducts(): Promise<{
  products: ProductModel[];
  flags: RegistrationFlagModel[];
  options: RegistrationOptionInput[];
}> {
  const category = (await api.getProductCategories()).find(({ isRegistration }) => isRegistration);

  const [products, form] = await Promise.all([
    category
      ? api
          .getFullProductsByCategory(category.id)
          .catch((error: Error) => (isAccessError(error) ? [] : fthrow(error)))
      : [],
    api.getRegistrationForm(),
  ]);

  return { products, flags: form.flags, options: form.options };
}

interface RegistrationCardProps {
  readonly userId: number;
  readonly registration?: UserRegistration;
  readonly bannedWords: string[];
  complete(): void;
  close(): void;
}

export const RegistrationEditModal: FC<RegistrationCardProps> = (props) => {
  return (
    <Modal className="modal-large" id="registrationCard" isOpen toggle={props.close}>
      <ModalHeader>Edit Registration</ModalHeader>
      <LoadingWrapper dataFetcher={getProducts} inline>
        {({ products, flags, options }) => {
          if (products.length === 0) {
            return <RegistrationNoProducts close={props.close} />;
          }

          return (
            <RegistrationCardInner
              {...props}
              attendanceTypes={products}
              flags={flags}
              options={options}
            />
          );
        }}
      </LoadingWrapper>
    </Modal>
  );
};

const RegistrationNoProducts: FC<{ close(): void }> = ({ close }) => {
  return (
    <>
      <ModalBody>No registration products available.</ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close}>
          Cancel
        </Button>
      </ModalFooter>
    </>
  );
};

interface RegistrationCardInnerProps extends RegistrationCardProps {
  readonly attendanceTypes: ProductModel[];
  readonly flags: RegistrationFlagModel[];
  readonly options: RegistrationOptionInput[];
}

const RegistrationCardInner: FC<RegistrationCardInnerProps> = ({
  userId,
  registration,
  attendanceTypes,
  bannedWords,
  flags,
  options,
  complete,
  close,
}) => {
  const [enabledFlags, setEnabledFlags] = useState<number[]>(
    registration ? registration.flags : [],
  );

  const [attendanceTypeId, setAttendanceTypeId] = useNumericInputState(
    attendanceTypes.find(({ id }) => id === registration?.attendanceType.id)?.id ??
      attendanceTypes[0].id,
    true,
  );

  const attendanceType = attendanceTypes.find(({ id }) => id === attendanceTypeId)!;

  return (
    <JSONForm
      method={registration ? 'patch' : 'post'}
      onSuccess={complete}
      path={
        registration ? `/api/registrations/${registration.id}` : `/api/users/${userId}/registration`
      }
    >
      <ModalBody>
        <Row>
          <Col lg={6} md={12}>
            <FormGroup>
              <Label for="badgeName">Badge Name</Label>
              <Input
                defaultValue={registration ? registration.badgeName : ''}
                invalid={bannedWords.includes('badgeName')}
                maxLength={32}
                name="badgeName"
              />
              {bannedWords.includes('badgeName') && (
                <FormFeedback>Badge name contains a banned word.</FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col lg={6} md={12}>
            <FormGroup>
              <Label for="attendanceType">Attendance Type</Label>
              <Input
                name="attendanceTypeId"
                onChange={setAttendanceTypeId}
                type="select"
                value={attendanceTypeId}
              >
                {attendanceTypes.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          {flags.length > 0 && (
            <Col lg={12}>
              <FormGroup>
                <AttendanceFlags
                  enabledFlags={enabledFlags}
                  flags={flags}
                  onUpdate={setEnabledFlags}
                />
              </FormGroup>
            </Col>
          )}
          {options.length > 0 && (
            <Col lg={12}>
              <FormProductOptions
                options={options.filter((t) => t.fromRegistration)}
                payload={registration?.options}
                title="Registration Options"
              />
            </Col>
          )}
          {attendanceType && attendanceType.options.length > 0 && (
            <Col lg={12}>
              <FormProductOptions
                options={attendanceType.options}
                payload={registration?.options}
                title="Product Options"
              />
            </Col>
          )}
          {attendanceType.raffle && (
            <Col lg={12}>
              <UpdateRaffleComponent ticket={registration?.raffleTicket} />
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        {registration?.paidOrderItem && (
          <div style={{ flexGrow: 1 }}>
            <ActionButtonModal
              actionContent="Delete"
              buttonContent={<>Remove payment</>}
              color="danger"
              id="deleteRegPayment"
              onComplete={async () => {
                await api.deleteRegistrationPayment(userId);
                toast.success('Payment deleted!');
                complete();
              }}
              title="Delete Registration Payment?"
            >
              Are you sure you want to delete the payment related to this registration?
            </ActionButtonModal>
            <TransferButton complete={complete} userId={userId} />
          </div>
        )}

        {registration && !registration.paidOrderItem && (
          <div style={{ flexGrow: 1 }}>
            <ActionButtonModal
              actionContent="Delete"
              buttonContent={<>Delete</>}
              color="danger"
              id="deleteReg"
              onComplete={async () => {
                await api.deleteRegistration(registration.id);
                toast.success('Registration deleted!');
                complete();
              }}
              title="Delete Registration?"
            >
              Are you sure you want to delete this registration?
            </ActionButtonModal>
          </div>
        )}

        <Button color="primary" id="submitUpdateReg" type="submit">
          {registration ? 'Update' : 'Create'} Registration
        </Button>
        <Button color="secondary" onClick={close}>
          Cancel
        </Button>
      </ModalFooter>
    </JSONForm>
  );
};

interface TransferButtonProps {
  readonly userId: number;
  complete(): void;
}

const TransferButton: FC<TransferButtonProps> = ({ userId, complete }) => {
  const [ids, setIds] = useState<number[]>([]);

  return (
    <ActionButtonModal
      actionContent="Transfer"
      buttonContent={<>Transfer</>}
      color="secondary"
      id="transferPayment"
      onComplete={async () => {
        await api.transferRegistration(userId, ids[0]);
        toast.success('Transfer completed!');
        complete();
      }}
      title="Transfer Registration Payment?"
    >
      Select user to transfer this registration to:
      <UserSelector id="transferSelect" maxItems={1} selectionIdsChanged={setIds} />
    </ActionButtonModal>
  );
};

interface FormProductOptionProps {
  readonly title: string;
  readonly options: ProductOptionInput[];
  readonly payload?: OptionDataPayload;
}

const FormProductOptions: FC<FormProductOptionProps> = ({ title, options, payload }) => {
  return (
    <>
      <hr />
      <FormGroup row>
        <Label md={4} sm={12}>
          {title}
        </Label>
        <Col md={8} sm={12}>
          {options.map((o) => (
            <Col key={o.id} xs={12}>
              <ProductOptionViewer
                defaultValue={payload ? payload[o.id.toString()] : undefined}
                option={o}
              />
            </Col>
          ))}
        </Col>
      </FormGroup>
    </>
  );
};

interface UpdateRaffleComponentProps {
  readonly ticket?: RegistrationTicket;
}

const UpdateRaffleComponent: FC<UpdateRaffleComponentProps> = ({ ticket }) => {
  return (
    <>
      <hr />
      <FormGroup row>
        <Label md={4} sm={12}>
          Silo Options
        </Label>
        <Col lg={8} md={12}>
          <FormGroup>
            <Label for="raffle[status]">Status</Label>
            <Input
              defaultValue={ticket?.status ?? 'waiting'}
              id="raffleStatus"
              name="raffle[status]"
              type="select"
            >
              {raffleTicketStatus.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="raffle[points]">Points</Label>
            <Input
              defaultValue={ticket?.points ?? 0}
              maxLength={32}
              name="raffle[points]"
              type="number"
            />
          </FormGroup>
        </Col>
      </FormGroup>
    </>
  );
};
